import type { ReactNode } from 'react'
import { RefineSnackbarProvider } from '@refinedev/mui'
import { RefineKbarProvider } from '@refinedev/kbar'
import CssBaseline from '@mui/material/CssBaseline'
import GlobalStyles from '@mui/material/GlobalStyles'
import { ColorModeContextProvider } from '@app/contexts/color-mode'
import { AuthProvider, useAuth } from '@app/contexts/auth'
import { EnumProvider } from '@modules/enums/context/enum'
import { AnalyticsProvider } from '@publisher/modules/analytics'
import { Refine } from '@refinedev/core'
import { API_BASE_URL, customDataProvider, shareDataProvider } from '@publisher/modules/api'
import routerBindings from '@refinedev/react-router'
import { authProvider } from './authProvider'
import { customNotificationProvider } from './notificationProvider'
import { refineConfig } from './config'

interface ProvidersProps {
  children: ReactNode
}

const EnumProviderWrapper = ({ children }: { children: ReactNode }) => {
  const { authData, authLoading } = useAuth()
  return (
    <EnumProvider
      apiBaseUrl={API_BASE_URL}
      isAuthenticated={!!authData?.authenticated}
      isAuthLoading={authLoading}
    >
      {children}
    </EnumProvider>
  )
}

export const Providers = ({ children }: ProvidersProps) => {
  return (
    <RefineKbarProvider>
      <ColorModeContextProvider>
        <CssBaseline />
        <GlobalStyles
          styles={{
            html: { WebkitFontSmoothing: 'auto' },
            body: {
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            },
            '*': {
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            },
          }}
        />
        <RefineSnackbarProvider>
          <Refine
            dataProvider={{ default: customDataProvider, share: shareDataProvider }}
            routerProvider={routerBindings}
            authProvider={authProvider}
            notificationProvider={customNotificationProvider}
            options={refineConfig.options}
            resources={refineConfig.resources}
          >
            <AuthProvider>
              <EnumProviderWrapper>
                <AnalyticsProvider>{children}</AnalyticsProvider>
              </EnumProviderWrapper>
            </AuthProvider>
          </Refine>
        </RefineSnackbarProvider>
      </ColorModeContextProvider>
    </RefineKbarProvider>
  )
}
